import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { JobProgressValue, JobCategory, Job } from '../JobTypes';
import dateDifferenceInHours from 'date-fns/differenceInHours';

const SERVICE_REQUEST_SEARCH_QUERY = gql`
query serviceRequestSearch($search: serviceRequestSearchInput!) {
  serviceRequestSearch(search:$search) {
    id
    requestNumber
    type
    status
    dateReceived    
  }
}
`;

interface ServiceRequestSearchResult {
  id: string
  requestNumber: string
  type: string
  status: string
  dateReceived: string
}
interface QueryData {
  serviceRequestSearch: [ServiceRequestSearchResult]
}
export enum RequestGroup {
  BinDelivery = "BinDelivery",
  MissedBin = "MissedBin",
}
export enum CompletionStatus {
  Complete = "Y",
  Incomplete = "N",
}
interface QueryVarsSearch {
  requestIds?: string[]
  requestGroups?: RequestGroup[]
  completionStatus?: CompletionStatus
  dateReceivedFrom: string
  dateReceivedTo: string
}
export interface QueryVars {
  search: QueryVarsSearch
}

const CURRENT_DATE: Date = new Date();

const useServiceRequestSearch = (queryVarsInput:QueryVars) => {
  const [queryVars, setQueryVars] = useState<QueryVars>(queryVarsInput);
  const { loading, error, data, fetchMore } = useQuery<QueryData, QueryVars>( SERVICE_REQUEST_SEARCH_QUERY, { 
    variables: queryVars,
    notifyOnNetworkStatusChange: true,
  });
  const [jobData, setJobData] = useState<Job[]>([]);
  const [resultCount, setResultCount] = useState<number>(-1);

  useEffect(()=>{
    fetchMore( { variables: queryVars } );
  }, [queryVars, fetchMore]);
  
  useEffect(()=>{
    if(!data)
      return;
    
    function getStateProgres(stateDescription: string):JobProgressValue {
      const stateProgressMapping: any = {
        'Open': JobProgressValue.Pending,
        'Unallocated': JobProgressValue.Pending,
        'Pending': JobProgressValue.Pending,
        'In Progress': JobProgressValue.InProgress,
        'Work Order In Progress': JobProgressValue.InProgress,
        'Request Completed': JobProgressValue.Complete,
        'Closed': JobProgressValue.Complete,
        'Cancelled': JobProgressValue.Complete,
      };
      return stateProgressMapping[stateDescription] || JobProgressValue.Pending;
    }
    
    function getJobCategory(serviceRequestDescription: string):JobCategory {
      if( serviceRequestDescription === 'Damaged or Broken Bin' 
        || serviceRequestDescription === 'Additional Waste Service' 
        || serviceRequestDescription === 'New Waste Service' 
        || serviceRequestDescription === 'Replace Stolen Bin' 
        || serviceRequestDescription === 'Cancel Additional Service Domestic Red' 
        || serviceRequestDescription === 'Cancellation Additional Green Waste' 
        || serviceRequestDescription === 'Additional Domestic Red Collection service' 
        || serviceRequestDescription === 'Cancellation Additional Yellow Waste' 
      ) {
        return JobCategory.BinDelivery;
      }
      if( serviceRequestDescription === 'Missed Bin' ) {
        return JobCategory.MissedBin;
      }
      return JobCategory.Unknown;
    }

    const tempJobData:Job[] = data.serviceRequestSearch.map((item: ServiceRequestSearchResult) => ({
      id: item.id,
      requestNumber: item.requestNumber,
      type: item.type,
      date: new Date(item.dateReceived),
      status: item.status,
      category: getJobCategory(item.type),
      progress: getStateProgres(item.status),
      overdue: ( dateDifferenceInHours( CURRENT_DATE, new Date(item.dateReceived) ) > 48 ),
    }));

    setResultCount(tempJobData.length);
    setJobData(tempJobData);
  }, [data,queryVars]);

  return { loading, error, jobData, resultCount, setQueryVars };
};

export default useServiceRequestSearch;
